import type { NASensorMapObject, NAMTObject } from '@/api/models';
import { NAMTMessageType } from '@/types/enums';
import { useFieldPending } from '@/hooks';
import { resolveOnlineState } from '@/lib/enum-resolvers';
import { ToggleSwitch, SelectDropdown, EditableNameField, SettingRow, PendingBadge } from '@/components/common/ConfigControls';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { Button } from '@/components/common/Button/Button';
import {
  getCombinedAddress,
  findPendingMT,
  ZONE_TYPE_OPTIONS,
  ZONE_TYPE_CATEGORY_OPTIONS,
  SECURITY_ZONE_TYPE,
  HIDE_INPUT_TYPE_FOR_ZONE_TYPES,
  SIREN_SYNC_OPTIONS,
  toOrdinal,
  LEGACY_ALARM_TYPE_STRING_MAP,
  LEGACY_DEVICE_CATEGORY_STRING_MAP,
  LEGACY_SIREN_SYNC_STRING_MAP,
  type XPulseZoneSlot,
} from '@/lib/sensor-commands';

// Mirrors XPulseSettingsPageViewModel.cs — this drill-down shows only the
// configurable settings mobile exposes on the xPulse settings page (name, siren
// sync, per-zone type/category/input-type), no telemetry/status display.
export function XPulseSensorConfig({
  sensor,
  zoneSlots,
  remoteSirenSyncMode,
  mtObjList,
  isExpanded,
  onToggleExpand,
  sendCommand,
  onUnpairRequest,
  onSetZoneCategory,
}: {
  sensor: NASensorMapObject;
  /** null = zone data not yet available (mirrors mobile's own all-or-nothing gate). */
  zoneSlots: XPulseZoneSlot[] | null;
  remoteSirenSyncMode: unknown;
  mtObjList: NAMTObject[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  sendCommand: (cmd: NAMTMessageType, dataStrList?: string[]) => Promise<boolean>;
  onUnpairRequest: (target: { msb: number; lsb: number; name: string }) => void;
  onSetZoneCategory: (zoneNum: number, deviceCategory: number) => Promise<void>;
}) {
  const fieldPending = useFieldPending();
  const combinedAddress = getCombinedAddress(sensor.sourceAddressMSB, sensor.sourceAddressLSB);
  // Any reported zone's onlineState reflects the physical sensor's connectivity --
  // NASensorMapObject itself carries no onlineState of its own.
  const isOffline = resolveOnlineState(zoneSlots?.find((s) => s.zone)?.zone?.onlineState) === 'Offline';

  const pendingName = findPendingMT(mtObjList, NAMTMessageType.SET_SENSOR_NAME, (d) => d[0] === combinedAddress);
  const pendingUnpair = findPendingMT(mtObjList, NAMTMessageType.UNPAIR_SENSOR, (d) => d[0] === combinedAddress);
  // Siren Sync is device-global — mobile's own pending lookup has no address/key
  // filter for it either (SET_WIRELESS_SIREN_SYNC_MODE, dataStrList[0] only).
  const pendingSiren = findPendingMT(mtObjList, NAMTMessageType.SET_WIRELESS_SIREN_SYNC_MODE);
  const sirenValue =
    fieldPending.getOptimisticValue<number>('siren') ??
    (pendingSiren ? Number(pendingSiren.dataStrList?.[0] ?? 0) : (toOrdinal(remoteSirenSyncMode, LEGACY_SIREN_SYNC_STRING_MAP) ?? 0));

  return (
    <div className="rounded-lg border border-slate-200 dark:border-navy-700">
      {/* Sensor header row */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggleExpand();
        }}
        className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-navy-800"
      >
        <div className="group flex items-center">
          <EditableNameField
            value={fieldPending.getOptimisticValue<string>('name') ?? pendingName?.dataStrList?.[1] ?? sensor.sensorName}
            pending={fieldPending.isPending('name') || !!pendingName}
            onSave={(newName) =>
              fieldPending.run('name', newName, () => sendCommand(NAMTMessageType.SET_SENSOR_NAME, [combinedAddress, newName]))
            }
          />
          <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
            Addr: {sensor.sourceAddressMSB}:{sensor.sourceAddressLSB}
          </span>
          {isOffline && (
            <span className="ml-2">
              <StatusBadge label="Offline" variant="danger" dot />
            </span>
          )}
          {pendingUnpair && <span className="ml-2 text-xs font-medium text-red-500">Deleting…</span>}
        </div>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isExpanded && !pendingUnpair && (
        <div className="space-y-4 border-t border-slate-200 bg-slate-50 px-4 py-4 dark:border-navy-700 dark:bg-navy-800/50">
          {zoneSlots === null ? (
            <p className="text-sm text-slate-400">
              Zone data not yet available for this sensor — waiting for the device to report in.
            </p>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-navy-600 dark:bg-navy-900">
                <SettingRow label="Siren Sync Setting" description="Applies device-wide, shown here as on the xPulse settings page.">
                  <div className="flex items-center">
                    <SelectDropdown
                      value={String(sirenValue)}
                      options={SIREN_SYNC_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                      onChange={(val) =>
                        fieldPending.run('siren', Number(val), () =>
                          sendCommand(NAMTMessageType.SET_WIRELESS_SIREN_SYNC_MODE, [val]),
                        )
                      }
                    />
                    {(fieldPending.isPending('siren') || pendingSiren) && <PendingBadge />}
                  </div>
                </SettingRow>
              </div>

              <div className="space-y-4">
                {zoneSlots.map((slot) => {
                  if (!slot.zone) {
                    return (
                      <div
                        key={slot.zoneCounter}
                        className="rounded-lg border border-slate-200 bg-white p-3 dark:border-navy-600 dark:bg-navy-900"
                      >
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Zone {slot.zoneCounter}</p>
                        <p className="mt-1 text-xs text-slate-400">No data reported for this zone yet.</p>
                      </div>
                    );
                  }

                  const zone = slot.zone;
                  const zoneKey = String(slot.zoneNum);
                  const zoneTypeFieldKey = `zoneType-${slot.zoneNum}`;
                  const zoneCategoryFieldKey = `zoneCategory-${slot.zoneNum}`;
                  const inputTypeFieldKey = `inputType-${slot.zoneNum}`;

                  const pendingZoneType = findPendingMT(mtObjList, NAMTMessageType.SET_SECURITY_ZONE_TYPE, (d) => d[0] === zoneKey);
                  const zoneTypeOrdinal =
                    fieldPending.getOptimisticValue<number>(zoneTypeFieldKey) ??
                    (pendingZoneType ? Number(pendingZoneType.dataStrList?.[1]) : toOrdinal(zone.alarmType, LEGACY_ALARM_TYPE_STRING_MAP));

                  const pendingInputType = findPendingMT(mtObjList, NAMTMessageType.SET_SECURITY_ZONE_INPUT_TYPE, (d) => d[0] === zoneKey);
                  const isNC =
                    fieldPending.getOptimisticValue<boolean>(inputTypeFieldKey) ??
                    (pendingInputType ? pendingInputType.dataStrList?.[1] === '1' : zone.isNC);

                  const categoryOrdinal =
                    fieldPending.getOptimisticValue<number>(zoneCategoryFieldKey) ??
                    toOrdinal(zone.deviceCategory, LEGACY_DEVICE_CATEGORY_STRING_MAP);

                  const showCategory = zoneTypeOrdinal === SECURITY_ZONE_TYPE;
                  const showInputType = !HIDE_INPUT_TYPE_FOR_ZONE_TYPES.includes(zoneTypeOrdinal ?? -1);

                  return (
                    <div
                      key={slot.zoneCounter}
                      className="rounded-lg border border-slate-200 bg-white p-3 dark:border-navy-600 dark:bg-navy-900"
                    >
                      <p className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">Zone {slot.zoneCounter}</p>

                      <div className={`grid gap-3 text-xs ${showCategory || showInputType ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">Zone Type</span>
                          <div className="flex items-center">
                            <SelectDropdown
                              value={String(zoneTypeOrdinal ?? '')}
                              options={ZONE_TYPE_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                              onChange={(val) =>
                                fieldPending.run(zoneTypeFieldKey, Number(val), async () => {
                                  await sendCommand(NAMTMessageType.SET_SECURITY_ZONE_TYPE, [zoneKey, val]);
                                  await sendCommand(NAMTMessageType.GET_SECURITY_DATA);
                                })
                              }
                            />
                            {(fieldPending.isPending(zoneTypeFieldKey) || pendingZoneType) && <PendingBadge />}
                          </div>
                        </div>

                        {showCategory && (
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Zone Type Category</span>
                            <div className="flex items-center">
                              <SelectDropdown
                                value={String(categoryOrdinal ?? '')}
                                options={ZONE_TYPE_CATEGORY_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                                disabled={fieldPending.isPending(zoneCategoryFieldKey)}
                                onChange={(val) =>
                                  fieldPending.run(zoneCategoryFieldKey, Number(val), () =>
                                    onSetZoneCategory(slot.zoneNum, Number(val)),
                                  )
                                }
                              />
                              {fieldPending.isPending(zoneCategoryFieldKey) && <PendingBadge />}
                            </div>
                          </div>
                        )}

                        {showInputType && (
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Zone Configuration</span>
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-slate-700 dark:text-slate-300">{isNC ? 'Normally Closed' : 'Normally Open'}</span>
                              <ToggleSwitch
                                enabled={isNC}
                                loading={false}
                                onToggle={() =>
                                  fieldPending.run(inputTypeFieldKey, !isNC, async () => {
                                    await sendCommand(NAMTMessageType.SET_SECURITY_ZONE_INPUT_TYPE, [zoneKey, isNC ? '0' : '1']);
                                    await sendCommand(NAMTMessageType.GET_SECURITY_DATA);
                                  })
                                }
                              />
                              {(fieldPending.isPending(inputTypeFieldKey) || pendingInputType) && <PendingBadge />}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  onUnpairRequest({
                    msb: sensor.sourceAddressMSB,
                    lsb: sensor.sourceAddressLSB,
                    name: sensor.sensorName,
                  })
                }
              >
                Unpair
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
